/**
 * CSP Products Carousel - Single card navigation
 * Custom element pour navigation du carousel produits
 */
class CspCarouselSingleNav extends HTMLElement {
  connectedCallback() {
    this.slideshow = this.closest('slideshow-component');
    if (!this.slideshow) return;

    this.prevBtn = this.querySelector('[data-nav-prev]');
    this.nextBtn = this.querySelector('[data-nav-next]');

    this.currentIndex = 0;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigatePrev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigateNext());
    }

    this.setupPagination();

    // Écouter les changements de slide (scroll manuel ou navigation)
    this.slideshow.addEventListener('slideshow:select', (e) => {
      this.currentIndex = e.detail.index;
      this.updateButtonStates();
      this.updatePagination();
    });

    // Observer les scroll manuels pour mettre à jour les dots
    const slidesContainer = this.slideshow.querySelector('slideshow-slides');
    if (slidesContainer) {
      let scrollTimeout;
      slidesContainer.addEventListener('scroll', () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updatePagination();
          this.updateButtonStates();
        }, 100);
      }, { passive: true });
    }

    // Initialiser l'état des boutons
    this.updateButtonStates();
    this.updatePagination();

    // Setup Intersection Observer to center items when all fit
    this.setupCenteringObserver();
  }

  getSlides() {
    return this.slideshow?.querySelectorAll('slideshow-slide:not([hidden])') || [];
  }

  getPagination() {
    // Support both csp-products-carousel and csp-linked-products-carousel
    const section = this.slideshow?.closest('.section-csp-products-carousel, .section-csp-linked-products-carousel');
    return section?.querySelector('[data-pagination]');
  }

  navigatePrev() {
    if (!this.slideshow) return;
    const newIndex = this.currentIndex - 1;
    if (newIndex >= 0) {
      this.slideshow.select(newIndex);
    }
  }

  navigateNext() {
    if (!this.slideshow) return;
    const slides = this.getSlides();
    const newIndex = this.currentIndex + 1;
    if (newIndex < slides.length) {
      this.slideshow.select(newIndex);
    }
  }

  updateButtonStates() {
    if (!this.prevBtn || !this.nextBtn || !this.slideshow) return;

    const slides = this.getSlides();
    const slidesContainer = this.slideshow.querySelector('slideshow-slides');

    if (!slidesContainer || !slides.length) return;

    const isInfinite = this.slideshow.hasAttribute('infinite');

    // Vérifier si on est au début
    const isAtStart = slidesContainer.scrollLeft <= 1;

    // Vérifier si on est à la fin du scroll
    // On compare scrollLeft + clientWidth avec scrollWidth
    const scrollRight = slidesContainer.scrollLeft + slidesContainer.clientWidth;
    const isAtEnd = scrollRight >= slidesContainer.scrollWidth - 2;

    this.prevBtn.disabled = !isInfinite && isAtStart;
    this.nextBtn.disabled = !isInfinite && isAtEnd;
  }

  setupPagination() {
    const pagination = this.getPagination();
    if (!pagination) return;

    pagination.querySelectorAll('.csp-carousel-pagination__dot').forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.scrollToSlide(index);
      });
    });
  }

  scrollToSlide(targetIndex) {
    const slides = this.getSlides();
    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    // Utiliser scrollIntoView pour une animation fluide
    targetSlide.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });

    // Ne pas mettre à jour currentIndex ici, laisser le scroll listener le faire
    // pour éviter les conflits entre animation et scroll manuel
  }

  updatePagination() {
    const pagination = this.getPagination();
    if (!pagination) return;

    const dots = pagination.querySelectorAll('.csp-carousel-pagination__dot');
    if (!dots.length) return;

    const slidesContainer = this.slideshow?.querySelector('slideshow-slides');
    if (!slidesContainer) return;

    const slides = this.getSlides();
    if (!slides.length) return;

    // Trouver quel slide est le plus visible/centré
    const containerRect = slidesContainer.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestIndex = 0;
    let minDistance = Infinity;

    slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - containerCenter);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    // Activer le dot correspondant
    dots.forEach((dot, index) => {
      if (index === closestIndex) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
  }

  setupCenteringObserver() {
    const slidesContainer = this.slideshow?.querySelector('slideshow-slides');
    if (!slidesContainer) return;

    const slides = this.getSlides();
    if (slides.length === 0) return;

    const lastSlide = slides[slides.length - 1];
    if (!lastSlide) return;

    // Check if centering is needed by comparing scrollWidth with clientWidth
    const checkCentering = () => {
      if (!slidesContainer) return;

      // If scrollWidth <= clientWidth, all slides fit without scrolling
      const allFit = slidesContainer.scrollWidth <= slidesContainer.clientWidth + 1; // +1 for rounding tolerance

      if (allFit) {
        slidesContainer.classList.add('is-centered');
      } else {
        slidesContainer.classList.remove('is-centered');
      }
    };

    // Create Intersection Observer to watch the last slide
    // Using viewport as root to detect when last slide is visible
    this.centeringObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When the last slide intersects with the container's viewport,
          // check if all slides fit
          checkCentering();
        });
      },
      {
        root: slidesContainer,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.5, 1.0], // Multiple thresholds for better detection
      }
    );

    // Observe the last slide
    this.centeringObserver.observe(lastSlide);

    // Also check on resize and scroll
    let resizeTimeout;
    this.resizeHandler = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        checkCentering();
      }, 150);
    };
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    // Check on scroll as well (in case slides are added/removed dynamically)
    let scrollTimeout;
    this.scrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        checkCentering();
      }, 100);
    };
    slidesContainer.addEventListener('scroll', this.scrollHandler, { passive: true });

    // Initial check after a short delay to ensure DOM is ready
    setTimeout(() => {
      checkCentering();
    }, 100);
  }

  disconnectedCallback() {
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.navigatePrev);
    }
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.navigateNext);
    }
    if (this.centeringObserver) {
      this.centeringObserver.disconnect();
      this.centeringObserver = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    const slidesContainer = this.slideshow?.querySelector('slideshow-slides');
    if (slidesContainer && this.scrollHandler) {
      slidesContainer.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }
}

customElements.define('csp-carousel-single-nav', CspCarouselSingleNav);
